import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { getToken, setToken as saveToken, clearToken } from './hiddenAuth';

// The local-model endpoint (Cloudflare Tunnel -> Mac proxy -> Ollama).
// Override for local dev with REACT_APP_LLM_BASE=http://localhost:8500
const LLM_BASE = process.env.REACT_APP_LLM_BASE || 'https://llm.fabianmorelli.de';
const CHATS_KEY = 'llm_chats';
const RETAIN_MS = 3 * 24 * 60 * 60 * 1000; // chats auto-delete after 3 days, unless kept

// connState: 'unknown' | 'online' | 'modelDown' | 'offline'
const STATUS_LABEL = {
  online: 'online',
  modelDown: 'model not loaded',
  offline: 'Mac offline',
  unknown: 'checking…',
};

// Map the raw Ollama model name from /health to a friendly display label, so the
// UI follows whichever model the proxy currently serves (clean model swaps).
function prettyModel(name) {
  if (!name) return 'Assistant';
  const base = String(name).split(':')[0];
  const KNOWN = { vibethinker: 'VibeThinker-3B', qwythos: 'Qwythos-9B', bonsai: 'Bonsai-2 27B' };
  return KNOWN[base] || base.charAt(0).toUpperCase() + base.slice(1);
}

// Thinking effort for models that support it (Bonsai). Values are the chat
// template's own; 'off' disables thinking entirely.
const EFFORTS = [
  { value: 'off', label: 'No thinking' },
  { value: 'low', label: 'Think: low' },
  { value: 'medium', label: 'Think: medium' },
  { value: 'xhigh', label: 'Think: high' },
];

// --- Chat persistence (localStorage) ---------------------------------------

// Reading window.localStorage throws a SecurityError when a browser blocks site
// storage (cookies/site data disabled, strict privacy mode, some extensions).
// Wrap every access so the app degrades gracefully (no persistence) instead of
// crashing the whole page.
const store = {
  get(key) { try { return localStorage.getItem(key); } catch (e) { return null; } },
  set(key, val) { try { localStorage.setItem(key, val); } catch (e) { /* ignore */ } },
  remove(key) { try { localStorage.removeItem(key); } catch (e) { /* ignore */ } },
};

// Chats now live on the Mac (shared across every logged-in device) instead of in
// localStorage. Drop any that expired — older than 3 days and not pinned/kept.
function pruneExpired(arr) {
  const cutoff = Date.now() - RETAIN_MS;
  return arr.filter((c) => c.retained || (c.updatedAt || c.createdAt || 0) > cutoff);
}

function chatTitle(messages) {
  const firstUser = messages.find((m) => m.role === 'user');
  const t = (firstUser ? firstUser.content : '').trim().replace(/\s+/g, ' ');
  if (!t) return firstUser?.images?.length ? 'Image' : 'New chat';
  return t.length > 42 ? t.slice(0, 42) + '…' : t;
}

// Kept chats first, then most-recently-updated.
function sortChats(a, b) {
  if (!!a.retained !== !!b.retained) return a.retained ? -1 : 1;
  return (b.updatedAt || 0) - (a.updatedAt || 0);
}

function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'c' + Date.now() + Math.random().toString(16).slice(2);
}

// --- Rendering helpers ------------------------------------------------------

// Reasoning models wrap their chain-of-thought in <think>…</think>. Separate it from the
// final answer so the reasoning can be tucked into a collapsible block.
function splitThink(content) {
  const start = content.indexOf('<think>');
  if (start === -1) return { think: null, answer: content, thinking: false };
  const before = content.slice(0, start);
  const rest = content.slice(start + 7); // '<think>'.length
  const end = rest.indexOf('</think>');
  if (end === -1) return { think: rest, answer: before, thinking: true }; // still streaming the reasoning
  const answer = (before + rest.slice(end + 8)).replace(/^\n+/, ''); // '</think>'.length
  return { think: rest.slice(0, end), answer, thinking: false };
}

// The model emits Markdown (GFM tables/headings) with LaTeX in \(..\), \[..\], or $..$.
// remark-math only understands $-delimiters, so normalize the backslash ones first,
// leaving fenced code blocks untouched.
function normalizeMath(src) {
  return src
    .split(/(```[\s\S]*?```)/g)
    .map((chunk, i) =>
      i % 2 === 1
        ? chunk
        : chunk
            .replace(/\\\[/g, '$$$$')
            .replace(/\\\]/g, '$$$$')
            .replace(/\\\(/g, '$$')
            .replace(/\\\)/g, '$$')
    )
    .join('');
}

const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [[rehypeKatex, { throwOnError: false, errorColor: '#e74c3c' }]];

function Markdown({ text }) {
  return (
    <div className="lab-markdown">
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS}>
        {normalizeMath(text)}
      </ReactMarkdown>
    </div>
  );
}

// Map a token logprob to a confidence tint: red (unsure) -> green (confident).
function probColor(logprob) {
  const p = Math.exp(logprob ?? 0); // 0..1
  const hue = Math.max(0, Math.min(120, p * 120));
  return `hsla(${hue}, 75%, 45%, 0.33)`;
}

// Visualizer view: raw generated tokens tinted by probability, click for top-k.
function TokenStream({ tokens, streaming, onTokenClick }) {
  return (
    <div className="lab-viz">
      {tokens.map((t, i) => (
        <span
          key={i}
          className="lab-tok"
          style={{ backgroundColor: probColor(t.logprob) }}
          title={`p=${(Math.exp(t.logprob ?? 0) * 100).toFixed(1)}%  (click for alternatives)`}
          onClick={(e) => onTokenClick(t, e)}
        >{t.text}</span>
      ))}
      {streaming && <span className="lab-viz-cursor">▋</span>}
    </div>
  );
}

// The normal rendered answer: folded reasoning + Markdown/LaTeX.
function RenderedAnswer({ content, streaming }) {
  const { think, answer, thinking } = splitThink(content);
  return (
    <>
      {think !== null && (thinking ? (
        <div className="lab-think-live">
          <span className="lab-think-label">Thinking…</span>{think}
        </div>
      ) : (
        <details className="lab-think">
          <summary>Reasoning</summary>
          <div className="lab-think-text"><Markdown text={think.trim()} /></div>
        </details>
      ))}
      {answer ? (
        <div className="lab-answer"><Markdown text={answer} /></div>
      ) : (think === null && streaming ? '…' : null)}
    </>
  );
}

// A scroll box that keeps its newest content in view while streaming, but yields
// to the reader: the moment you scroll up it stops following, and resumes only
// once you scroll back to the bottom. Lets you read while generation continues.
function StickyScroll({ className, follow, children }) {
  const ref = useRef(null);
  const pinned = useRef(true);  // follow the bottom?
  const lastTop = useRef(0);    // scrollTop we last set, to spot a user scroll-up

  // Runs at commit, before paint, on every streamed token. A real scroll-up makes
  // scrollTop drop below where we left it (new tokens are appended *below*), so we
  // detect it by position delta — robust even when scroll events lag the stream.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.scrollTop < lastTop.current - 4) pinned.current = false;
    if (follow && pinned.current) el.scrollTop = el.scrollHeight;
    lastTop.current = el.scrollTop;
  });

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) pinned.current = true; // back at bottom
    else if (el.scrollTop < lastTop.current - 4) pinned.current = false;              // scrolled up
    lastTop.current = el.scrollTop;
  };

  return (
    <div className={className} ref={ref} onScroll={onScroll}>
      {children}
    </div>
  );
}

function AssistantBody({ content, tokens, streaming, onTokenClick }) {
  if (tokens && tokens.length) {
    // Probabilities on: colored tokens (left) and the rendered output (right),
    // each in its own independently-scrollable pane.
    return (
      <div className="lab-msg-body lab-sbs">
        <div className="lab-sbs-col">
          <div className="lab-sbs-label">tokens · click for alternatives</div>
          <StickyScroll className="lab-sbs-scroll" follow={streaming}>
            <TokenStream tokens={tokens} streaming={streaming} onTokenClick={onTokenClick} />
          </StickyScroll>
        </div>
        <div className="lab-sbs-col">
          <div className="lab-sbs-label">rendered</div>
          <StickyScroll className="lab-sbs-scroll" follow={streaming}>
            <RenderedAnswer content={content} streaming={streaming} />
          </StickyScroll>
        </div>
      </div>
    );
  }
  return (
    <div className="lab-msg-body">
      <RenderedAnswer content={content} streaming={streaming} />
    </div>
  );
}

// Shrink an image file in the browser to a JPEG whose longer side is at most
// `max` px, and return its base64 (no data: prefix — Ollama's images convention).
// Keeps uploads and the saved chat history small; Bonsai sees 1024 px anyway.
async function shrinkImage(file, max = 1024) {
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bmp.width * k);
  canvas.height = Math.round(bmp.height * k);
  canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close?.();
  return canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
}

const MAX_IMAGES = 6; // per conversation, matches the Bonsai server

// Grow the input with its content, up to a cap (then it scrolls internally).
function autoGrow(el) {
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 200) + 'px';
}

export default function Lab() {
  const [token, setToken] = useState(getToken);
  const [phase, setPhase] = useState(() => (getToken() ? 'unlocked' : 'locked'));
  const [connState, setConnState] = useState('unknown');
  const [modelAvailable, setModelAvailable] = useState(true);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(() => store.get('llm_model') || '');
  const [effortModels, setEffortModels] = useState([]);
  const [effort, setEffort] = useState(() => store.get('llm_effort') || 'medium');
  const [visionModels, setVisionModels] = useState([]);
  const [agentModels, setAgentModels] = useState([]);
  const [loadedModels, setLoadedModels] = useState([]); // models holding the Mac's memory
  const [unloading, setUnloading] = useState(false);
  const [webOn, setWebOn] = useState(() => store.get('llm_web') === '1');
  const [pendingImages, setPendingImages] = useState([]); // base64 JPEGs for the next message
  const [imageError, setImageError] = useState('');
  const fileRef = useRef(null);
  const [lockError, setLockError] = useState('');
  const [passInput, setPassInput] = useState('');

  const [messages, setMessages] = useState([]); // { role: 'user' | 'assistant', content }
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);

  const [chats, setChats] = useState([]);
  const [currentId, setCurrentId] = useState(newId);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [vizOn, setVizOn] = useState(false);
  const [tokPopover, setTokPopover] = useState(null);
  const [temperature, setTemperature] = useState(() => {
    const v = parseFloat(store.get('llm_temp'));
    return Number.isFinite(v) ? v : 1.0;
  });

  const abortRef = useRef(null);
  const messagesRef = useRef(null);
  const messagesPinned = useRef(true); // auto-scroll only while the user is at the bottom
  const messagesLastTop = useRef(0);   // last scrollTop we set, to detect user scroll-ups
  const taRef = useRef(null);

  // Probe /health. Returns one of: 'online' | 'modelDown' | 'offline' | 'unauthorized'
  const checkHealth = useCallback(async (tok) => {
    try {
      const res = await fetch(`${LLM_BASE}/health`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.status === 401) return 'unauthorized';
      if (res.status === 503) return 'modelDown';
      if (!res.ok) return 'offline';
      const data = await res.json();
      const avail = data.available_models || (data.model ? [String(data.model).split(':')[0]] : []);
      setAvailableModels(avail);
      setEffortModels(data.effort_models || []);
      setVisionModels(data.vision_models || []);
      setAgentModels(data.agent_models || []);
      setLoadedModels(data.loaded || []);
      setModelAvailable(avail.length > 0);
      setSelectedModel((cur) => {
        if (cur && avail.includes(cur)) return cur;        // keep a valid saved choice
        const def = data.default_model || data.model || '';
        return String(def).split(':')[0] || cur;           // else the server default
      });
      return 'online';
    } catch (e) {
      return 'offline'; // network error — Mac off or tunnel down
    }
  }, []);

  // Persist the shared chat list to the Mac. Fire-and-forget; a failed save
  // (Mac offline) just means the next successful load resyncs.
  const persistChats = useCallback((next) => {
    const tok = getToken();
    if (!tok) return;
    fetch(`${LLM_BASE}/chats`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
      body: JSON.stringify({ chats: next }),
    }).catch(() => { /* offline — will resync on next load */ });
  }, []);

  // On unlock, load the shared chats from the Mac. One-time migration: if the
  // server has none yet but this browser has old local chats, seed from them.
  useEffect(() => {
    if (phase !== 'unlocked' || !token) return;
    let cancelled = false;
    fetch(`${LLM_BASE}/chats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { chats: [] }))
      .then((d) => {
        if (cancelled) return;
        let arr = Array.isArray(d.chats) ? d.chats : [];
        let migrated = false;
        if (arr.length === 0) {
          try {
            const local = JSON.parse(store.get(CHATS_KEY));
            if (Array.isArray(local) && local.length) { arr = local; migrated = true; }
          } catch (e) { /* ignore */ }
        }
        const kept = pruneExpired(arr);
        setChats(kept);
        if (migrated || kept.length !== arr.length) persistChats(kept);
      })
      .catch(() => { /* Mac offline — leave chats empty until reachable */ });
    return () => { cancelled = true; };
  }, [phase, token, persistChats]);

  // On mount with a saved token, go straight to chat and verify in the background.
  useEffect(() => {
    if (phase !== 'unlocked' || !token) return;
    let cancelled = false;
    checkHealth(token).then((result) => {
      if (cancelled) return;
      if (result === 'unauthorized') {
        clearToken();
        setToken('');
        setPhase('locked');
        setLockError('Saved password was rejected. Please re-enter it.');
      } else {
        setConnState(result);
      }
    });
    return () => { cancelled = true; };
  }, [phase, token, checkHealth]);

  // Keep the newest message in view as it streams — but yield the instant the user
  // scrolls up to read. Detected by scroll-position delta (new content is appended
  // below, so a real scroll-up lowers scrollTop), which survives fast streaming
  // where scroll events can't keep up. Resumes following at the bottom.
  useLayoutEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    if (el.scrollTop < messagesLastTop.current - 4) messagesPinned.current = false;
    if (messagesPinned.current) el.scrollTop = el.scrollHeight;
    messagesLastTop.current = el.scrollTop;
  }, [messages]);

  const onMessagesScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) messagesPinned.current = true;
    else if (el.scrollTop < messagesLastTop.current - 4) messagesPinned.current = false;
    messagesLastTop.current = el.scrollTop;
  };

  // Persist the active conversation to the saved-chat list once a turn finishes.
  // Strip per-token viz data — history keeps only role/content (keeps storage small).
  useEffect(() => {
    if (streaming || messages.length === 0) return;
    const stripped = messages.map((m) =>
      m.images?.length ? { role: m.role, content: m.content, images: m.images } : { role: m.role, content: m.content });
    setChats((prev) => {
      const now = Date.now();
      const idx = prev.findIndex((c) => c.id === currentId);
      let next;
      if (idx === -1) {
        next = [
          { id: currentId, title: chatTitle(stripped), messages: stripped, createdAt: now, updatedAt: now, retained: false },
          ...prev,
        ];
      } else {
        next = prev.slice();
        next[idx] = { ...next[idx], messages: stripped, title: next[idx].title || chatTitle(stripped), updatedAt: now };
      }
      persistChats(next);
      return next;
    });
  }, [messages, streaming, currentId, persistChats]);

  async function unlock(e) {
    e.preventDefault();
    const tok = passInput.trim();
    if (!tok) return;
    setLockError('');
    const result = await checkHealth(tok);
    if (result === 'unauthorized') {
      setLockError('Wrong password.');
      return;
    }
    if (result === 'offline') {
      setLockError("Can't reach your Mac — is it on with the model + tunnel running?");
      return;
    }
    // online or modelDown -> token is valid, let them in
    saveToken(tok);
    setToken(tok);
    setConnState(result);
    setPassInput('');
    setPhase('unlocked');
  }

  function lock() {
    abortRef.current?.abort();
    clearToken();
    setToken('');
    setMessages([]);
    setPhase('locked');
  }

  function newChat() {
    abortRef.current?.abort();
    setStreaming(false);
    setCurrentId(newId());
    setMessages([]);
  }

  function openChat(id) {
    if (id === currentId) return;
    abortRef.current?.abort();
    setStreaming(false);
    const c = chats.find((x) => x.id === id);
    if (!c) return;
    setCurrentId(id);
    messagesPinned.current = true;
    messagesLastTop.current = 0;
    setMessages(c.messages || []);
  }

  function deleteChat(id) {
    setChats((prev) => {
      const next = prev.filter((c) => c.id !== id);
      persistChats(next);
      return next;
    });
    if (id === currentId) {
      setCurrentId(newId());
      setMessages([]);
    }
  }

  function toggleRetain(id) {
    setChats((prev) => {
      const next = prev.map((c) => (c.id === id ? { ...c, retained: !c.retained } : c));
      persistChats(next);
      return next;
    });
  }

  // Replace the trailing assistant placeholder's content (and viz tokens, if any).
  const setAssistant = (content, tokens) =>
    setMessages((prev) => {
      const copy = prev.slice();
      copy[copy.length - 1] = tokens
        ? { role: 'assistant', content, tokens }
        : { role: 'assistant', content };
      return copy;
    });

  const canSeeImages = visionModels.includes(selectedModel);
  const agentOn = webOn && agentModels.includes(selectedModel);
  const imagesInChat = messages.reduce((n, m) => n + (m.images?.length || 0), 0);

  async function addImages(files) {
    const imgs = [...files].filter((f) => f.type.startsWith('image/'));
    if (!imgs.length) return;
    const room = MAX_IMAGES - imagesInChat - pendingImages.length;
    if (imgs.length > room) setImageError(`At most ${MAX_IMAGES} images per chat.`);
    else setImageError('');
    try {
      const shrunk = await Promise.all(imgs.slice(0, Math.max(0, room)).map((f) => shrinkImage(f)));
      setPendingImages((prev) => [...prev, ...shrunk]);
    } catch {
      setImageError("Couldn't read that image.");
    }
  }

  function onPaste(e) {
    if (!canSeeImages) return;
    const files = [...(e.clipboardData?.files || [])];
    if (files.some((f) => f.type.startsWith('image/'))) { e.preventDefault(); addImages(files); }
  }

  // Hand the Mac's memory back. The model loads again by itself on the next message.
  async function unloadModels() {
    if (unloading || streaming) return;
    setUnloading(true);
    try {
      const res = await fetch(`${LLM_BASE}/unload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setLoadedModels([]);
    } catch (e) { /* offline: the health poll will correct the state */ }
    setUnloading(false);
  }

  async function send() {
    const text = input.trim();
    const imgs = canSeeImages ? pendingImages : [];
    if ((!text && !imgs.length) || streaming) return;

    const userMsg = imgs.length ? { role: 'user', content: text, images: imgs } : { role: 'user', content: text };
    const convo = [...messages, userMsg];
    setMessages([...convo, { role: 'assistant', content: '' }]);
    messagesPinned.current = true; messagesLastTop.current = 0; // a fresh turn always scrolls into view
    setInput('');
    setPendingImages([]);
    setImageError('');
    if (taRef.current) taRef.current.style.height = 'auto';
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    let acc = '';        // the answer / final content
    let thinkAcc = '';   // separated reasoning (Ollama streams a 'thinking' field)
    const toks = [];
    // Re-fold reasoning into the inline <think>…</think> form the renderer expects.
    // Leave the tag open while only reasoning has arrived (live "Thinking…"); close
    // it once the answer starts.
    const display = () =>
      !thinkAcc ? acc : acc ? `<think>${thinkAcc}</think>${acc}` : `<think>${thinkAcc}`;

    const reqBody = { messages: convo.map(({ tokens, ...m }) => m), options: { temperature } };
    if (selectedModel) reqBody.model = selectedModel;
    if (effortModels.includes(selectedModel)) reqBody.effort = effort;
    if (selectedModel) setLoadedModels((prev) => prev.includes(selectedModel) ? prev : [...prev, selectedModel]);
    if (agentOn) reqBody.agent = true;
    else if (vizOn) { reqBody.logprobs = true; reqBody.top_logprobs = 5; }

    try {
      const res = await fetch(`${LLM_BASE}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(reqBody),
        signal: controller.signal,
      });

      if (res.status === 401) {
        setAssistant('[Your password is no longer valid — locking.]');
        setTimeout(lock, 1200);
        return;
      }
      if (!res.ok || !res.body) {
        setConnState(res.status === 503 ? 'modelDown' : 'offline');
        setAssistant('[The model is unreachable right now.]');
        return;
      }
      setConnState('online');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (!line) continue;
          let obj;
          try { obj = JSON.parse(line); } catch { continue; }
          if (obj.status === 'loading') {
            // the model isn't in memory yet (Bonsai starts on demand)
            if (!acc && !thinkAcc) {
              setAssistant(`*Loading ${prettyModel(obj.model)} into memory — the first reply takes a few extra seconds…*`);
            }
            continue;
          }
          if (obj.error) {
            setConnState(obj.error === 'ollama_unreachable' ? 'offline' : 'modelDown');
            const errText = `\n\n[error: ${obj.detail || obj.error}]`;
            acc += errText;
            if (vizOn) toks.push({ text: errText, logprob: -99, top: [] });
            setAssistant(display(), vizOn ? toks : undefined);
            continue;
          }
          const delta = obj.message?.content || '';
          const thinkDelta = obj.message?.thinking || '';
          if (thinkDelta) thinkAcc += thinkDelta;
          if (obj.logprob !== undefined && obj.logprob !== null) {
            toks.push({ text: delta, logprob: obj.logprob, top: obj.top_logprobs || [] });
          }
          if (delta) acc += delta;
          if (thinkDelta || delta || obj.logprob !== undefined) {
            setAssistant(display(), vizOn ? toks : undefined);
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setConnState('offline');
        setAssistant(display() + '\n\n[connection lost]');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
      checkHealth(token);   // refresh what's holding memory, for the Unload button
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  if (phase === 'locked') {
    return (
      <div className="lab-lock">
        <form className="lab-lock-card" onSubmit={unlock}>
          <h1>Private Lab</h1>
          <p className="lab-sub">Enter the password to talk to the local model.</p>
          <input
            type="password"
            value={passInput}
            onChange={(e) => setPassInput(e.target.value)}
            placeholder="Password"
            autoFocus
          />
          <button type="submit">Unlock</button>
          {lockError && <p className="lab-error">{lockError}</p>}
        </form>
      </div>
    );
  }

  const sortedChats = [...chats].sort(sortChats);
  const modelLabel = prettyModel(selectedModel);

  return (
    <div className="lab-shell">
      <aside className={`lab-sidebar${sidebarOpen ? '' : ' collapsed'}`}>
        <button className="lab-newchat" onClick={newChat}>+ New chat</button>
        <div className="lab-chatlist">
          {sortedChats.length === 0 && <p className="lab-chatlist-empty">No saved chats yet.</p>}
          {sortedChats.map((c) => (
            <div
              key={c.id}
              className={`lab-chatitem${c.id === currentId ? ' active' : ''}`}
              onClick={() => openChat(c.id)}
              title={c.title}
            >
              <span className="lab-chatitem-title">{c.title}</span>
              <span className="lab-chatitem-actions">
                <button
                  className={`lab-chat-pin${c.retained ? ' on' : ''}`}
                  title={c.retained ? "Kept — won't auto-delete" : 'Keep (prevent 3-day auto-delete)'}
                  onClick={(e) => { e.stopPropagation(); toggleRetain(c.id); }}
                >📌</button>
                <button
                  className="lab-chat-del"
                  title="Delete chat"
                  onClick={(e) => { e.stopPropagation(); deleteChat(c.id); }}
                >×</button>
              </span>
            </div>
          ))}
        </div>
        <p className="lab-sidebar-note">Chats auto-delete after 3 days unless you keep them 📌</p>
      </aside>

      <div className="lab">
        <div className="lab-bar">
          <button className="lab-menu" onClick={() => setSidebarOpen((v) => !v)} title="Toggle chat list">☰</button>
          <span className="lab-title">Private Lab</span>
          <button
            className={`lab-viz-toggle${vizOn ? ' on' : ''}`}
            onClick={() => setVizOn((v) => !v)}
            title="Color new answers by token probability"
          >Probs</button>
          {agentModels.includes(selectedModel) && (
            <button
              className={`lab-viz-toggle${webOn ? ' on' : ''}`}
              onClick={() => setWebOn((v) => { store.set('llm_web', v ? '0' : '1'); return !v; })}
              title="Let the model search the web and read pages before answering (slower)"
            >Web</button>
          )}
          {availableModels.length > 1 && (
            <select
              className="lab-model"
              value={selectedModel}
              onChange={(e) => { setSelectedModel(e.target.value); store.set('llm_model', e.target.value); }}
              title="Choose which local model to chat with"
            >
              {availableModels.map((m) => (
                <option key={m} value={m}>{prettyModel(m)}</option>
              ))}
            </select>
          )}
          {effortModels.includes(selectedModel) && (
            <select
              className="lab-model"
              value={effort}
              onChange={(e) => { setEffort(e.target.value); store.set('llm_effort', e.target.value); }}
              title="How long the model reasons before answering — higher is slower but more careful"
            >
              {EFFORTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
          <span className={`lab-status lab-status-${connState}`}>
            <span className="lab-dot" /> {STATUS_LABEL[connState] || connState}
          </span>
          {loadedModels.length > 0 && (
            <button
              className="lab-lockbtn"
              onClick={unloadModels}
              disabled={unloading || streaming}
              title={`${loadedModels.map(prettyModel).join(', ')} ${loadedModels.length > 1 ? 'are' : 'is'} in the Mac's memory — free it (it reloads on your next message)`}
            >{unloading ? 'Unloading…' : 'Unload'}</button>
          )}
          <button className="lab-lockbtn" onClick={lock}>Lock</button>
        </div>

        {connState === 'offline' && (
          <div className="lab-banner">
            Can't reach the model. Make sure your Mac is on and Ollama, the proxy, and the tunnel are running.
          </div>
        )}
        {connState === 'modelDown' && (
          <div className="lab-banner">
            The proxy is up but Ollama isn't responding — start Ollama on your Mac.
          </div>
        )}
        {connState === 'online' && !modelAvailable && (
          <div className="lab-banner">
            The model isn't loaded in Ollama yet. Create it on your Mac with <code>ollama create</code>.
          </div>
        )}

        <div className="lab-messages" ref={messagesRef} onScroll={onMessagesScroll}>
          {messages.length === 0 && (
            <p className="lab-empty">Ask {modelLabel} a math, coding, or reasoning question.</p>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`lab-msg lab-msg-${m.role}`}>
              <div className="lab-msg-role">{m.role === 'user' ? 'You' : modelLabel}</div>
              {m.role === 'assistant' ? (
                <AssistantBody
                  content={m.content}
                  tokens={m.tokens}
                  streaming={streaming && i === messages.length - 1}
                  onTokenClick={(t, e) => setTokPopover({ ...t, x: e.clientX, y: e.clientY })}
                />
              ) : (
                <div className="lab-msg-body">
                  {m.images?.length > 0 && (
                    <div className="lab-msg-images">
                      {m.images.map((b, j) => <img key={j} src={`data:image/jpeg;base64,${b}`} alt="" />)}
                    </div>
                  )}
                  {m.content}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="lab-controls">
          <label className="lab-temp" title="Sampling temperature — lower is more focused, higher is more random">
            <span>temp</span>
            <input
              type="range"
              min="0"
              max="2"
              step="0.05"
              value={temperature}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setTemperature(v);
                store.set('llm_temp', String(v));
              }}
            />
            <span className="lab-temp-val">{temperature.toFixed(2)}</span>
          </label>
        </div>
        {canSeeImages && (pendingImages.length > 0 || imageError) && (
          <div className="lab-attach-row">
            {pendingImages.map((b, j) => (
              <span key={j} className="lab-attach">
                <img src={`data:image/jpeg;base64,${b}`} alt="" />
                <button
                  title="Remove"
                  onClick={() => setPendingImages((prev) => prev.filter((_, k) => k !== j))}
                >×</button>
              </span>
            ))}
            {imageError && <span className="lab-attach-error">{imageError}</span>}
          </div>
        )}
        <div
          className="lab-input"
          onDragOver={canSeeImages ? (e) => e.preventDefault() : undefined}
          onDrop={canSeeImages ? (e) => { e.preventDefault(); addImages(e.dataTransfer.files); } : undefined}
        >
          {canSeeImages && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => { addImages(e.target.files); e.target.value = ''; }}
              />
              <button
                className="lab-attach-btn"
                onClick={() => fileRef.current?.click()}
                disabled={streaming}
                title="Attach images (or paste / drop them here)"
              >+ Image</button>
            </>
          )}
          <textarea
            ref={taRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); autoGrow(e.target); }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            placeholder="Message  (Enter to send, Shift+Enter for newline)"
            rows={1}
          />
          {streaming ? (
            <button className="lab-stop" onClick={() => abortRef.current?.abort()}>Stop</button>
          ) : (
            <button onClick={send} disabled={!input.trim() && !(canSeeImages && pendingImages.length)}>Send</button>
          )}
        </div>

        {tokPopover && (
          <>
            <div className="lab-pop-backdrop" onClick={() => setTokPopover(null)} />
            <div
              className="lab-pop"
              style={{
                left: Math.min(tokPopover.x, window.innerWidth - 252),
                top: Math.min(tokPopover.y + 12, window.innerHeight - 220),
              }}
            >
              <div className="lab-pop-head">
                chose <code>{JSON.stringify(tokPopover.text)}</code> · {(Math.exp(tokPopover.logprob ?? 0) * 100).toFixed(1)}%
              </div>
              {(tokPopover.top || []).map((a, i) => {
                const p = Math.exp(a.logprob);
                return (
                  <div key={i} className="lab-pop-row">
                    <code className="lab-pop-tok">{JSON.stringify(a.token)}</code>
                    <span className="lab-pop-track"><span className="lab-pop-bar" style={{ width: `${Math.max(2, p * 100)}%` }} /></span>
                    <span className="lab-pop-pct">{(p * 100).toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
